<template>
  <ErrorBoundary :owners="['bot-squad-search-and-recommendation-front']">
    <ProductCardCarouselSkeleton
      v-if="
        recommendationsStatus === 'idle' || recommendationsStatus === 'pending'
      "
      v-bind="$attrs"
      :with-title="!!title"
    />
    <section
      v-else-if="recommendations.length"
      data-qa="recommendation-carousel"
      data-test="recommendation-carousel"
      v-bind="$attrs"
    >
      <ProductCardCarousel
        :product-granularity
        :products="recommendations"
        :subtitle
        :title
        :tracking="{ ...trackingData, provider: 'algolia', list }"
        v-on="withAddToCart ? { addToCart: addToCartHandler } : {}"
      >
        <template #post-card="slotProps">
          <slot name="post-card" v-bind="slotProps" />
        </template>
      </ProductCardCarousel>
    </section>
  </ErrorBoundary>
</template>

<script lang="ts" setup>
import { watch } from 'vue'

import type { ProductGranularity } from '@backmarket/http-api/src/api-specs-content/shared/product-granularity'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'

import { useAlgoliaRecommendations } from '../composables/useAlgoliaRecommendations/useAlgoliaRecommendations'
import type {
  AlgoliaRecommendationsModel,
  AlgoliaRecommendationsObjectIds,
  AlgoliaRecommendationsOptions,
} from '../composables/useAlgoliaRecommendations/useAlgoliaRecommendations.types'
import {
  type ExtraTrackingData,
  useListTrackingData,
} from '../composables/useProductTracking'

import ProductCardCarousel from './ProductCardCarousel/ProductCardCarousel.vue'
import ProductCardCarouselSkeleton from './ProductCardCarousel/ProductCardCarouselSkeleton.vue'

export type RecommendationAlgoliaCarouselProps = {
  recommendationQuery: {
    objectIds: AlgoliaRecommendationsObjectIds
    model: AlgoliaRecommendationsModel
    options?: AlgoliaRecommendationsOptions
  }
  title?: string
  subtitle?: string
  withAddToCart?: boolean
  trackingData?: ExtraTrackingData
  clientOnly?: boolean
  blockPosition?: number
  productGranularity?: ProductGranularity
  cache?: boolean
}

const {
  title,
  subtitle,
  recommendationQuery,
  withAddToCart = false,
  trackingData = {},
  clientOnly,
  blockPosition,
  productGranularity,
  cache,
} = defineProps<RecommendationAlgoliaCarouselProps>()

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits(['error', 'loaded', 'refresh', 'added-to-cart'])
const {
  data: recommendations,
  error: recommendationsError,
  status: recommendationsStatus,
} = useAlgoliaRecommendations(
  () => recommendationQuery.objectIds,
  recommendationQuery.model,
  () => recommendationQuery.options,
  { server: !clientOnly, cache },
)

const list = useListTrackingData({ blockPosition })

function addToCartHandler({
  status,
  product,
}: {
  status: 'success' | 'error'
  product: { listingId: string }
}) {
  if (status === 'success') {
    emit('refresh', product.listingId)
    emit('added-to-cart', product)
  }
}

watch(
  recommendationsStatus,
  () => {
    if (recommendationsStatus.value === 'success') {
      if (!recommendations.value.length) {
        emit('error', null)

        return
      }
      emit('loaded', recommendations.value)
    } else if (recommendationsStatus.value === 'error') {
      emit('error', recommendationsError.value)
    }
  },
  { immediate: true },
)
</script>
